import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import ImageCard from './ImageCard';
import UploadProgress from './UploadProgress';
import { useImageUpload } from './use-image-upload';
import { makeCss } from '../../../../knock-design-system';
import { IMAGE_DIALOG_TYPES } from '../constants';
import {
  DIALOG_CARD_LABELS,
  PHOTO_UPLOAD_KEYS,
  UPLOAD_STATUS,
} from './constants';

const useCss = makeCss({
  imageCard: {
    '& .MuiPaper-root.MuiPaper-outlined': {
      borderStyle: 'dashed',
    },
  },
  uploadProgressWrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255,255,255, 0.7)',
    '&.error': {
      backgroundColor: 'rgba(255, 220, 230, 0.9)',
    },
  },
});

const ImageUploadCard = forwardRef(
  (
    {
      file,
      fileUrl,
      deleteImageFile,
      isDescriptionVisible,
      onImageUpload,
      photoUploadKey,
    },
    ref,
  ) => {
    const css = useCss();

    const {
      abortImageUpload,
      description,
      progressValue,
      resetUpload,
      setDescription,
      uploadImage,
      uploadStatus,
    } = useImageUpload({ file, onImageUpload, photoUploadKey });

    useImperativeHandle(ref, () => ({
      abortImageUpload,
      uploadImage,
    }));

    const { name: fileName, size: fileSize } = file;
    const isFailureStatus = uploadStatus === UPLOAD_STATUS.FAILURE;

    return (
      <ImageCard
        cardAriaLabel={DIALOG_CARD_LABELS.QUEUE}
        description={description}
        css={css.imageCard}
        deleteImageFile={deleteImageFile}
        imageUrl={fileUrl}
        isDescriptionVisible={isDescriptionVisible}
        updateDescription={setDescription}
      >
        <div
          className={isFailureStatus ? 'error' : ''}
          css={css.uploadProgressWrapper}
          role={isFailureStatus ? 'alert' : 'status'}
        >
          <UploadProgress
            abortUpload={abortImageUpload}
            fileName={fileName}
            fileSize={fileSize}
            progressValue={progressValue}
            resetUpload={resetUpload}
            uploadStatus={uploadStatus}
          />
        </div>
      </ImageCard>
    );
  },
);

ImageUploadCard.displayName = 'ImageUploadCard';

ImageUploadCard.propTypes = {
  file: PropTypes.object,
  fileUrl: PropTypes.string,
  deleteImageFile: PropTypes.func,
  isDescriptionVisible: PropTypes.bool,
  onImageUpload: PropTypes.func,
  photoUploadKey: PropTypes.oneOf([
    PHOTO_UPLOAD_KEYS[IMAGE_DIALOG_TYPES.LOGO],
    PHOTO_UPLOAD_KEYS[IMAGE_DIALOG_TYPES.PHOTOS],
  ]),
};

export default ImageUploadCard;
